import React, { useState, useEffect } from 'react';
import { FiTable, FiCheck, FiX, FiRotateCcw } from 'react-icons/fi';

const AccountingTableQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {
  console.log('=== AccountingTableQuestion component rendered ===');
  console.log('Received question prop:', question);
  console.log('Question ID:', question?.id);
  console.log('Question type:', question?.type);
  console.log('Question text length:', question?.text?.length);
  console.log('Question text preview:', question?.text ? question.text.substring(0, 200) + '...' : 'null');
  
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [parsedTable, setParsedTable] = useState(null);

  // Parse table data string into structured format
  const parseTableData = (tableData) => {
    if (!tableData) return null;
    
    console.log('parseTableData called with:', tableData);
    
    const lines = tableData.split('\n').filter(line => line.trim());
    console.log('Split lines:', lines);
    
    if (lines.length === 0) return null;
    
    // Skip the first line if it's just a title (doesn't contain |)
    let dataLines = lines;
    let title = '';
    
    if (lines[0] && !lines[0].includes('|')) {
      title = lines[0];
      dataLines = lines.slice(1);
      console.log('First line is title:', title);
      console.log('Data lines start from:', dataLines);
    }
    
    if (dataLines.length === 0) return null;
    
    // Extract headers from first data line
    const headers = dataLines[0].split('|').map(h => h.trim());
    console.log('Extracted headers:', headers);
    
    // Create rows from remaining data lines
    const rows = dataLines.slice(1).map((line, index) => {
      console.log(`Processing data line ${index + 1}:`, line);
      
      const parts = line.split('|').map(cell => cell.trim());
      console.log(`Data line ${index + 1} parts:`, parts);
      
      if (parts.length >= headers.length) {
        const row = {};
        headers.forEach((header, headerIndex) => {
          row[header] = parts[headerIndex] || '';
        });
        console.log(`Created row ${index + 1}:`, row);
        return row;
      } else {
        console.log(`Data line ${index + 1} has insufficient parts:`, parts.length, 'expected:', headers.length);
        return null;
      }
    }).filter(Boolean);
    
    const result = { headers, rows, title };
    console.log('Final parsed result:', result);
    return result;
  };

  // Parse table data from question text when tableData is empty
  const parseTableFromText = (questionText) => {
    if (!questionText) return null;
    
    console.log('Attempting to parse table from question text:', questionText);
    
    // DYNAMIC APPROACH: Try to find any structured data patterns
    
    // 1. Check for T-account format (Trade payables)
    let tableMatch = questionText.match(/Trade payables general ledger account \(summarised\)\nDescription \| Dr \(\$\) \| Cr \(\$\)\n([\s\S]*?)\nTotal \| ([\d,]+) \| ([\d,]+)/);
    
    if (tableMatch) {
      console.log('T-account pattern found:', tableMatch);
      
      const tableContent = tableMatch[1];
      const totalDr = tableMatch[2];
      const totalCr = tableMatch[3];
      
      console.log('Table content to parse:', tableContent);
      
      const lines = tableContent.split('\n').filter(line => line.trim());
      console.log('Individual lines to parse:', lines);
      
      const rows = lines.map((line, index) => {
        console.log(`Parsing line ${index}: "${line}"`);
        const parts = line.split('|').map(part => part.trim());
        console.log(`Line ${index} parts:`, parts);
        
        if (parts.length >= 3) {
          const row = {
            description: parts[0],
            dr: parts[1] || '',
            cr: parts[2] || ''
          };
          console.log(`Created row ${index}:`, row);
          return row;
        }
        console.log(`Line ${index} has insufficient parts, skipping`);
        return null;
      }).filter(Boolean);
      
      console.log('Final parsed rows:', rows);
      return { rows, totalDr, totalCr };
    }
    
    // 2. Check for generic pipe-separated tables
    const pipeLines = questionText.split('\n').filter(line => line.includes('|'));
    if (pipeLines.length >= 2) {
      console.log('Generic pipe-separated table found');
      return parseTableData(questionText);
    }
    
    // 3. Check for colon-separated data
    const colonLines = questionText.split('\n').filter(line => line.includes(':'));
    if (colonLines.length >= 2) {
      console.log('Colon-separated data found');
      const headers = ['Item', 'Value'];
      const rows = colonLines.map(line => {
        const [key, value] = line.split(':').map(part => part.trim());
        return { [headers[0]]: key, [headers[1]]: value };
      });
      return { headers, rows };
    }
    
    // 4. Check for any structured data with numbers and symbols
    const structuredLines = questionText.split('\n').filter(line => 
      line.match(/[\d@$=]|units|kg|m|cm|mm|km|g|mg|ml|l|gal|pt|qt|cup|tbsp|tsp|oz|lb|ton|kmh|mph|rpm|db|hz|watt|volt|amp|ohm|f|k|c|bar|psi|pa|atm|joule|calorie|btu|wh|kwh|ev|mev|gev|tev|pev|ev|zv|yv/)
    );
    
    if (structuredLines.length >= 2) {
      console.log('Structured data with numbers/symbols found');
      const headers = ['Data'];
      const rows = structuredLines.map(line => ({ [headers[0]]: line.trim() }));
      return { headers, rows };
    }
    
    // 5. Last resort: create table from all lines
    const allLines = questionText.split('\n').filter(line => line.trim());
    if (allLines.length >= 2) {
      console.log('Creating table from all available lines');
      const headers = ['Content'];
      const rows = allLines.map(line => ({ [headers[0]]: line.trim() }));
      return { headers, rows };
    }
    
    return null;
  };

  useEffect(() => {
    console.log('=== useEffect started ===');
    console.log('question.id:', question.id);
    console.log('question.tableData:', question.tableData);
    console.log('question.text:', question.text);
    console.log('question.type:', question.type);
    
    // Check if question.text contains table-like patterns
    console.log('question.text contains |:', question.text ? question.text.includes('|') : false);
    console.log('question.text contains units:', question.text ? question.text.includes('units') : false);
    console.log('question.text contains @:', question.text ? question.text.includes('@') : false);
    console.log('question.text contains $:', question.text ? question.text.includes('$') : false);
    console.log('Full question.text:', question.text);
    
    let parsed = null;
    
    // PRIORITY 1: Check tableData first (most reliable source)
    if (question.tableData && question.tableData.trim()) {
      console.log('Attempting to parse from question.tableData (PRIORITY)...');
      parsed = parseTableData(question.tableData);
      console.log('Parsed from tableData:', parsed);
    }
    
    // PRIORITY 2: If no tableData, try question.text
    if (!parsed && question.text) {
      console.log('Attempting to parse from question.text (fallback)...');
      parsed = parseTableFromText(question.text);
      console.log('Parsed from question.text:', parsed);
    }
    
    // If still no table, check if the data might be embedded in options or other fields
    if (!parsed && question.options && question.options.length > 0) {
      console.log('Checking question.options for table data...');
      console.log('Options:', question.options);
      
      // Look for any option that might contain table-like data
      const optionWithData = question.options.find(opt => 
        opt.text && (
          opt.text.includes('units') || 
          opt.text.includes('@') || 
          opt.text.includes('$') ||
          opt.text.includes('Opening inventory') ||
          opt.text.includes('Purchases') ||
          opt.text.includes('Sales') ||
          opt.text.includes('Closing inventory')
        )
      );
      
      if (optionWithData) {
        console.log('Found option with potential table data:', optionWithData);
        // Try to parse the option text as table data
        parsed = parseTableFromText(optionWithData.text);
        console.log('Parsed from option text:', parsed);
      }
    }
    
    console.log('Final parsed result:', parsed);
    console.log('Setting parsedTable state to:', parsed);
    setParsedTable(parsed);
    console.log('=== useEffect completed ===');
  }, [question.id, question.tableData, question.text, question.type]);

  const handleOptionChange = (optionId) => {
    if (isSubmitted) return;

    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      } else {
        return [...prev, optionId];
      }
    });
  };

  const clearSelection = () => {
    if (isSubmitted) return;
    setSelectedOptions([]);
  };

  const validateSelection = () => {
    if (selectedOptions.length === 0) {
      alert('Please select at least one statement about the table before submitting.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateSelection()) return;

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        selectedOptions,
        type: 'ACCOUNTING_TABLE',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting accounting table answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getOptionStatus = (optionId) => {
    if (!showFeedback || !currentAnswer) return null;
    
    const isSelected = selectedOptions.includes(optionId);
    const option = question.options.find(opt => opt.id === optionId);
    
    if (isSelected && option.isCorrect) {
      return 'correct';
    } else if (isSelected && !option.isCorrect) {
      return 'incorrect';
    } else if (!isSelected && option.isCorrect) {
      return 'missed';
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      case 'missed': return 'border-orange-500 bg-orange-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheck className="text-green-600 text-lg" />;
      case 'incorrect': return <FiX className="text-red-600 text-lg" />;
      case 'missed': return <FiX className="text-orange-600 text-lg" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'correct': return 'Correct';
      case 'incorrect': return 'Incorrect';
      case 'missed': return 'Missed';
      default: return '';
    }
  };

  // Render T-Account format for accounting questions
  const renderTAccount = () => {
    console.log('renderTAccount called');
    console.log('Component state parsedTable:', parsedTable);
    
    // Use the component's state parsedTable instead of creating a new one
    if (!parsedTable || !parsedTable.rows || parsedTable.rows.length === 0) {
      console.log('No parsed table data available');
      return null; // Don't show table if no relevant data
    }
    
    // Check if this is T-account format (has totalDr and totalCr or Dr/Cr headers)
    const hasTotalDrCr = !!(parsedTable.totalDr && parsedTable.totalCr);
    const hasDrCrHeaders = !!(parsedTable.headers && parsedTable.headers.some(h => h.includes('Dr') && h.includes('Cr')));
    const isTAccountFormat = hasTotalDrCr || hasDrCrHeaders;
    
    console.log('T-account format check:');
    console.log('- Has totalDr/totalCr:', hasTotalDrCr);
    console.log('- Has Dr/Cr headers:', hasDrCrHeaders);
    console.log('- Is T-account format:', isTAccountFormat);
    
    if (isTAccountFormat) {
      console.log('Rendering T-account format');
      console.log('T-account data:', { totalDr: parsedTable.totalDr, totalCr: parsedTable.totalCr, rowsCount: parsedTable.rows?.length });
      
      // If we have headers with Dr/Cr, we need to parse the data differently
      if (parsedTable.headers && parsedTable.headers.some(h => h.includes('Dr') && h.includes('Cr'))) {
        console.log('Parsing T-account data from headers format');
        console.log('Headers:', parsedTable.headers);
        console.log('Rows:', parsedTable.rows);
        
        // Find the Dr and Cr column indices
        const drIndex = parsedTable.headers.findIndex(h => h.includes('Dr'));
        const crIndex = parsedTable.headers.findIndex(h => h.includes('Cr'));
        const descIndex = parsedTable.headers.findIndex(h => !h.includes('Dr') && !h.includes('Cr'));
        
        console.log('Column indices - Description:', descIndex, 'Dr:', drIndex, 'Cr:', crIndex);
        
        return (
          <div className="mb-8">
            <h4 className="text-xl font-bold text-gray-800 mb-6 text-center">
              {parsedTable.title || question.tableTitle || "Trade payables general ledger account (summarised)"}
            </h4>
            
            <div className="bg-gray-100 overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-white">
                    <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                      Description
                    </th>
                    <th className="py-3 text-end font-bold text-gray-800 text-base">
                      Dr ($)
                    </th>
                    <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                      Description
                    </th>
                    <th className="py-3 text-end font-bold text-gray-800 text-base">
                      Cr ($)
                    </th>
                  </tr>
                  <tr className="bg-white">
                    <th className="px-4 text-left font-bold text-gray-800 text-base">
                      
                    </th>
                    <th className="text-end font-bold text-gray-800 text-base">
                      $
                    </th>
                    <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                      
                    </th>
                    <th className="py-3 text-end font-bold text-gray-800 text-base">
                      $
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Data Rows */}
                  {parsedTable.rows.map((row, index) => {
                    const desc = descIndex >= 0 ? row[parsedTable.headers[descIndex]] || '' : '';
                    const dr = drIndex >= 0 ? row[parsedTable.headers[drIndex]] || '' : '';
                    const cr = crIndex >= 0 ? row[parsedTable.headers[crIndex]] || '' : '';
                    
                    console.log(`Row ${index}: desc="${desc}", dr="${dr}", cr="${cr}"`);
                    
                    return (
                      <tr key={index} className="bg-gray-100">
                        <td className="px-4 py-3 text-gray-800 text-base">
                          {desc}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-800 text-base font-medium">
                          {dr}
                        </td>
                        <td className="px-4 py-3 text-gray-800 text-base">
                          
                        </td>
                        <td className="px-4 py-3 text-right text-gray-800 text-base font-medium">
                          {cr}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Total Row - calculate from data */}
                  <tr className="bg-gray-200">
                    <td className="px-4 py-3 text-gray-800 text-base font-bold">
                      Total
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 text-base font-bold">
                      {parsedTable.rows.reduce((sum, row) => {
                        const dr = drIndex >= 0 ? row[parsedTable.headers[drIndex]] || '' : '';
                        const drValue = dr && !isNaN(parseFloat(dr.replace(/[^\d.-]/g, ''))) ? parseFloat(dr.replace(/[^\d.-]/g, '')) : 0;
                        return sum + drValue;
                      }, 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                      Total
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 text-base font-bold">
                      {parsedTable.rows.reduce((sum, row) => {
                        const cr = crIndex >= 0 ? row[parsedTable.headers[crIndex]] || '' : '';
                        const crValue = cr && !isNaN(parseFloat(cr.replace(/[^\d.-]/g, ''))) ? parseFloat(cr.replace(/[^\d.-]/g, '')) : 0;
                        return sum + crValue;
                      }, 0).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      }
      
      // Original T-account format with totalDr/totalCr
      console.log('Rendering original T-account format with totalDr/totalCr');
      console.log('Table title:', question.tableTitle || "Trade payables general ledger account (summarised)");
      console.log('Number of rows to render:', parsedTable.rows.length);
      
      return (
        <div className="mb-8">
          <h4 className="text-xl font-bold text-gray-800 mb-6 text-center">
            {question.tableTitle || "Trade payables general ledger account (summarised)"}
          </h4>
          
          <div className="bg-gray-100 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white">
                  <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                    Description
                  </th>
                  <th className="py-3 text-end font-bold text-gray-800 text-base">
                    Dr ($)
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                    Description
                  </th>
                  <th className="py-3 text-end font-bold text-gray-800 text-base">
                    Cr ($)
                  </th>
                </tr>
                <tr className="bg-white">
                  <th className="px-4 text-left font-bold text-gray-800 text-base">
                    
                  </th>
                  <th className="text-end font-bold text-gray-800 text-base">
                    $
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                    
                  </th>
                  <th className="py-3 text-end font-bold text-gray-800 text-base">
                    $
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Data Rows */}
                {parsedTable.rows.map((row, index) => (
                  <tr key={index} className="bg-gray-100">
                    <td className="px-4 py-3 text-gray-800 text-base">
                      {row.description}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 text-base font-medium">
                      {row.dr || ''}
                    </td>
                    <td className="px-4 py-3 text-gray-800 text-base">
                      
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 text-base font-medium">
                      {row.cr || ''}
                    </td>
                  </tr>
                ))}
                
                {/* Total Row */}
                <tr className="bg-gray-200">
                  <td className="px-4 py-3 text-gray-800 text-base font-bold">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right text-gray-800 text-base font-bold">
                    {parsedTable.totalDr}
                  </td>
                  <td className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right text-gray-800 text-base font-bold">
                    {parsedTable.totalCr}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    console.log('T-account rendering completed, returning table component');
    
    // Check if this is generic table format (has headers)
    if (parsedTable.headers) {
      console.log('Rendering generic table format');
      console.log('Headers found:', parsedTable.headers);
      console.log('Rows found:', parsedTable.rows);
      
      // Check if this is an inventory movements table
      if (parsedTable.headers.some(header => 
        header.toLowerCase().includes('units') || 
        header.toLowerCase().includes('price') || 
        header.toLowerCase().includes('cost of sales') ||
        header.toLowerCase().includes('total')
      )) {
        console.log('Detected inventory movements table format');
        console.log('Will render inventory table with headers:', parsedTable.headers);
        console.log('Will render inventory table with rows:', parsedTable.rows);
        
        return (
          <div className="mb-8">
            <h4 className="text-xl font-bold text-gray-800 mb-6 text-center">
              {parsedTable.title || question.tableTitle || "Inventory Movements - October 20X9"}
            </h4>
            
            <div className="bg-gray-100 overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-white">
                    {parsedTable.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedTable.rows.map((row, index) => (
                    <tr key={index} className="bg-gray-100">
                      {parsedTable.headers.map((header, headerIndex) => (
                        <td key={headerIndex} className="px-4 py-3 text-gray-800 text-base">
                          {row[header] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
      
      // Check if this is a financial statement table
      if (parsedTable.headers.some(header => 
        header.toLowerCase().includes('wallace co') || 
        header.toLowerCase().includes('bruce co') ||
        header.toLowerCase().includes('$000') ||
        header.toLowerCase().includes('non-current assets')
      )) {
        console.log('Detected financial statement table format');
        return (
          <div className="mb-8">
            <h4 className="text-xl font-bold text-gray-800 mb-6 text-center">
              {question.tableTitle || "Statements of Financial Position"}
            </h4>
            
            <div className="bg-gray-100 border border-gray-300 rounded-lg overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-white">
                    {parsedTable.headers.map((header, index) => (
                      <th key={index} className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-800 text-base">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedTable.rows.map((row, index) => (
                    <tr key={index} className="bg-gray-100">
                      {parsedTable.headers.map((header, headerIndex) => (
                        <td key={headerIndex} className="border border-gray-300 px-4 py-3 text-gray-800 text-base">
                          {row[header] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
      
      // Default generic table format
      console.log('Using default generic table format');
      console.log('Default table headers:', parsedTable.headers);
      console.log('Default table rows:', parsedTable.rows);
      
      return (
        <div className="mb-8">
          <h4 className="text-xl font-bold text-gray-800 mb-6 text-center">
            {parsedTable.title || question.tableTitle || "Data Table"}
          </h4>
          
          <div className="bg-gray-100 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white">
                  {parsedTable.headers.map((header, index) => (
                    <th key={index} className="px-4 py-3 text-left font-bold text-gray-800 text-base">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedTable.rows.map((row, index) => (
                  <tr key={index} className="bg-gray-100">
                    {parsedTable.headers.map((header, headerIndex) => (
                      <td key={headerIndex} className="px-4 py-3 text-gray-800 text-base">
                        {row[header] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    // If no table format is detected, show a message
    console.log('No table format detected, showing fallback message');
    return (
      <div className="mb-8 p-4 bg-gray-100 rounded-lg">
        <p className="text-gray-600 text-center">Question Content</p>
      </div>
    );
  };

  // Main render
  if (!question) {
    return <div className="text-red-500">No question provided</div>;
  }

  console.log('About to call renderTAccount()');
  console.log('Current parsedTable state:', parsedTable);
  console.log('parsedTable type:', typeof parsedTable);
  console.log('parsedTable keys:', parsedTable ? Object.keys(parsedTable) : 'null');
  console.log('parsedTable.rows:', parsedTable?.rows);
  console.log('parsedTable.totalDr:', parsedTable?.totalDr);
  console.log('parsedTable.totalCr:', parsedTable?.totalCr);
  console.log('parsedTable.headers:', parsedTable?.headers);

  const tableComponent = renderTAccount();
  console.log('renderTAccount result:', tableComponent);
  console.log('tableComponent type:', typeof tableComponent);
  console.log('tableComponent is null:', tableComponent === null);
  console.log('tableComponent is React element:', tableComponent && typeof tableComponent === 'object' && tableComponent.$$typeof);

  return (
    <div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {question.text}
        </h3>
      </div>

      {/* Table Display */}
      {tableComponent}

      {/* Options */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">
          Select all correct statements about the table:
        </h4>
        
        <div className="space-y-3">
          {question.options?.map((option) => {
            const status = getOptionStatus(option.id);
            
            return (
              <label key={option.id} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedOptions.includes(option.id) 
                  ? getStatusColor(status) 
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedOptions.includes(option.id)}
                  onChange={() => handleOptionChange(option.id)}
                  disabled={isSubmitted}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                
                <div className="flex-1">
                  <span className="text-gray-800 font-medium">{option.text}</span>
                </div>

                {/* Status Display */}
                {showFeedback && status && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      status === 'correct' 
                        ? 'bg-gray-100 text-green-700' 
                        : status === 'incorrect'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {getStatusText(status)}
                    </span>
                    {getStatusIcon(status)}
                  </div>
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      {!isSubmitted && (
        <div className="flex justify-center items-center mb-6">
          <button
            onClick={clearSelection}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiRotateCcw className="text-sm" />
            Clear Selection
          </button>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-blue-800 font-medium">Selected statements:</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-blue-600">{selectedOptions.length}</span>
            <span className="text-blue-600 font-medium">/</span>
            <span className="text-xl font-semibold text-blue-800">{question.options?.length || 0}</span>
          </div>
        </div>
      </div>

      {/* Feedback Display */}
      {showFeedback && currentAnswer && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-800 mb-3">Your Answer:</h4>
          <div className="space-y-2">
            {question.options?.map((option) => {
              const isSelected = selectedOptions.includes(option.id);
              const status = getOptionStatus(option.id);
              
              return (
                <div key={option.id} className="flex items-center gap-3 text-sm">
                  <span className={`font-medium ${
                    status === 'correct' ? 'text-green-600' : 
                    status === 'incorrect' ? 'text-red-600' : 
                    status === 'missed' ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                    {isSelected ? '✓' : '○'} {option.text}
                  </span>
                  {status && (
                    <span className={`text-xs px-2 py-1 rounded ${
                      status === 'correct' 
                        ? 'bg-gray-100 text-green-700' 
                        : status === 'incorrect'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {getStatusText(status)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingTableQuestion; 